import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
  ShowChartOutlined as ShowChartOutlinedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import SearchableSelect from '../../components/SearchableSelect';
import ProveedorSelectorField from '../../components/ProveedorSelectorField';
import { Proveedor } from '../../services/proveedorService';
import DemandaHistoricaDialog from '../../components/compras/DemandaHistoricaDialog';
import StockNetoComposicionDialog from '../../components/compras/StockNetoComposicionDialog';
import ExportProgressDialog, { ExportProgressState } from '../../components/shared/ExportProgressDialog';
import { catalogosService } from '../../services/catalogosService';
import comprasAnalisisService, {
  ComprasAnalisisEstado,
  ComprasAnalisisItem,
  PrioridadAnalisis,
} from '../../services/comprasAnalisisService';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatCurrency } from '../../utils/formatters';

type LineaEditada = { cantidad: number };

const DecimalInput: React.FC<{ value: number; onChange: (value: number) => void }> = ({ value, onChange }) => (
  <TextField
    size="small"
    value={Number.isFinite(value) ? value : 0}
    onChange={(e) => {
      const raw = e.target.value.replace(',', '.');
      const parsed = Number(raw);
      onChange(Number.isFinite(parsed) ? parsed : 0);
    }}
    inputProps={{ inputMode: 'decimal', min: 0, step: 1 }}
    sx={{ width: 120 }}
  />
);

const prioridadChips: Array<{ key: PrioridadAnalisis; label: string }> = [
  { key: 'acomprar', label: 'A comprar' },
  { key: 'critico', label: 'Críticos' },
  { key: 'sobrestock', label: 'Sobrestock' },
  { key: 'sinmovimiento', label: 'Sin movimiento' },
  { key: 'todos', label: 'Todos' },
];

const formatFecha = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-AR');
};

const describeFiltrosCalculo = (estado: ComprasAnalisisEstado | null) => {
  const f = estado?.filtrosCalculo;
  if (!f) return 'Sin filtros';
  const partes: string[] = [];
  if (f.nroProveedor) partes.push(`Proveedor ${f.nroProveedor}`);
  if (f.codigoRamo) partes.push(`Ramo ${f.codigoRamo}`);
  if (f.codigoLinea) partes.push(`Línea ${f.codigoLinea}`);
  if (f.codigoClase) partes.push(`Clase ${f.codigoClase}`);
  partes.push(`Horizonte ${f.horizonteDias ?? 60}d`);
  const ajuste = f.ajusteDemandaPct ?? 0;
  if (ajuste !== 0) {
    partes.push(`Ajuste demanda ${ajuste > 0 ? '+' : ''}${ajuste} %`);
  }
  return partes.length > 0 ? partes.join(' · ') : 'Todos los artículos inventariados';
};

const SugerenciaCompra: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [nroProveedor, setNroProveedor] = useState<number | undefined>(undefined);
  const [nombreProveedor, setNombreProveedor] = useState('');
  const [cuitProveedor, setCuitProveedor] = useState('');
  const [tipoIvaProveedor, setTipoIvaProveedor] = useState('');
  const [codigoRamo, setCodigoRamo] = useState('');
  const [codigoLinea, setCodigoLinea] = useState('');
  const [codigoClase, setCodigoClase] = useState('');
  const [horizonteDias, setHorizonteDias] = useState(60);
  const [ajusteDemandaPct, setAjusteDemandaPct] = useState(0);
  const [prioridad, setPrioridad] = useState<PrioridadAnalisis>('acomprar');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const [estado, setEstado] = useState<ComprasAnalisisEstado | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState<ComprasAnalisisItem[]>([]);
  const [kpis, setKpis] = useState({ itemsCriticos: 0, itemsAComprar: 0, importeEstimado: 0, coberturaMediana: 0 });
  const [ediciones, setEdiciones] = useState<Record<string, LineaEditada>>({});
  const [ramos, setRamos] = useState<Array<{ value: string; label: string }>>([]);
  const [lineas, setLineas] = useState<Array<{ value: string; label: string }>>([]);
  const [clases, setClases] = useState<Array<{ value: string; label: string }>>([]);
  const [exportando, setExportando] = useState(false);
  const [exportProgressOpen, setExportProgressOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgressState>({ message: '', currentPage: 0, totalPages: 0 });
  const [demandaDialogOpen, setDemandaDialogOpen] = useState(false);
  const [demandaDialogItem, setDemandaDialogItem] = useState<ComprasAnalisisItem | null>(null);
  const [stockNetoDialogOpen, setStockNetoDialogOpen] = useState(false);
  const [stockNetoDialogItem, setStockNetoDialogItem] = useState<ComprasAnalisisItem | null>(null);

  const handleProveedorSelect = (prov: Proveedor) => {
    setNroProveedor(prov.nroProveedor);
    setNombreProveedor(prov.nombre || '');
    setCuitProveedor(prov.cuit || '');
    setTipoIvaProveedor(prov.tipoIva || '');
    setPage(0);
  };

  const handleProveedorClear = () => {
    setNroProveedor(undefined);
    setNombreProveedor('');
    setCuitProveedor('');
    setTipoIvaProveedor('');
    setPage(0);
  };

  const snapshotReady = estado?.status === 'ready' || (estado?.isRecalculating ?? false);

  const loadCatalogos = useCallback(async () => {
    const [ramosResp, lineasResp, clasesResp] = await Promise.all([
      catalogosService.getRamos(1, 1000, ''),
      catalogosService.getLineas(1, 1000, '', undefined),
      catalogosService.getClases(1, 1000, '', undefined, undefined),
    ]);
    setRamos((ramosResp.items || []).map((x) => ({ value: x.codigoRamo, label: `${x.codigoRamo} - ${x.nombreRamo}` })));
    setLineas((lineasResp.items || []).map((x) => ({ value: x.codigoLinea, label: `${x.codigoLinea} - ${x.nombreLinea}` })));
    setClases((clasesResp.items || []).map((x) => ({ value: x.codigoClase, label: `${x.codigoClase} - ${x.nombreClase}` })));
  }, []);

  const loadEstado = useCallback(async () => {
    try {
      const data = await comprasAnalisisService.getEstado();
      setEstado(data);
      return data;
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo obtener el estado del cálculo.'), { variant: 'error' });
      return null;
    }
  }, [enqueueSnackbar]);

  const fetchGrid = useCallback(async () => {
    try {
      setLoadingGrid(true);
      const data = await comprasAnalisisService.getSugerencias({
        nroProveedor,
        codigoRamo: codigoRamo || undefined,
        codigoLinea: codigoLinea || undefined,
        codigoClase: codigoClase || undefined,
        prioridad,
        searchTerm: searchTerm.trim() || undefined,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      });
      setRows(data.items || []);
      setTotalCount(data.totalCount || 0);
      setKpis(data.kpis || { itemsCriticos: 0, itemsAComprar: 0, importeEstimado: 0, coberturaMediana: 0 });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cargar la grilla.'), { variant: 'error' });
    } finally {
      setLoadingGrid(false);
    }
  }, [
    nroProveedor,
    codigoRamo,
    codigoLinea,
    codigoClase,
    prioridad,
    searchTerm,
    page,
    rowsPerPage,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    void loadCatalogos();
    void loadEstado();
  }, [loadCatalogos, loadEstado]);

  useEffect(() => {
    const f = estado?.filtrosCalculo;
    if (!f) return;
    if (f.horizonteDias != null) setHorizonteDias(f.horizonteDias);
    setAjusteDemandaPct(Number(f.ajusteDemandaPct) || 0);
  }, [estado?.filtrosCalculo]);

  useEffect(() => {
    if (!snapshotReady) return;
    void fetchGrid();
  }, [
    nroProveedor,
    codigoRamo,
    codigoLinea,
    codigoClase,
    prioridad,
    searchTerm,
    page,
    rowsPerPage,
    snapshotReady,
    fetchGrid,
  ]);

  const rowsConEdicion = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        cantidadFinal: ediciones[row.codigoArticulo]?.cantidad ?? row.cantidadSugerida,
      })),
    [rows, ediciones],
  );

  const setCantidadEditada = (codigoArticulo: string, cantidad: number) => {
    setEdiciones((prev) => ({
      ...prev,
      [codigoArticulo]: { cantidad: Math.max(0, cantidad) },
    }));
  };

  const handleCalcular = async () => {
    try {
      setCalculando(true);
      setEdiciones({});
      const result = await comprasAnalisisService.calcular({
        nroProveedor,
        codigoRamo: codigoRamo || undefined,
        codigoLinea: codigoLinea || undefined,
        codigoClase: codigoClase || undefined,
        horizonteDias,
        ajusteDemandaPct,
      });
      setEstado(result);
      setPage(0);
      enqueueSnackbar(`Cálculo finalizado. ${result.totalItems ?? 0} artículos analizados.`, {
        variant: 'success',
        autoHideDuration: 6000,
      });
      await fetchGrid();
    } catch (error) {
      await loadEstado();
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo completar el cálculo.'), { variant: 'error', autoHideDuration: 8000 });
    } finally {
      setCalculando(false);
    }
  };

  const fetchAllFiltradas = useCallback(async () => {
    const pageSize = 200;
    let pageNumber = 1;
    let totalPages = 1;
    const allRows: ComprasAnalisisItem[] = [];
    setExportProgressOpen(true);
    do {
      setExportProgress({
        message: 'Leyendo snapshot...',
        currentPage: pageNumber,
        totalPages,
      });
      const resp = await comprasAnalisisService.getSugerencias({
        nroProveedor,
        codigoRamo: codigoRamo || undefined,
        codigoLinea: codigoLinea || undefined,
        codigoClase: codigoClase || undefined,
        prioridad,
        searchTerm: searchTerm.trim() || undefined,
        pageNumber,
        pageSize,
      });
      allRows.push(...(resp.items || []));
      totalPages = resp.totalPages || 1;
      pageNumber += 1;
    } while (pageNumber <= totalPages);
    return allRows;
  }, [nroProveedor, codigoRamo, codigoLinea, codigoClase, prioridad, searchTerm]);

  const handleExportExcel = async () => {
    if (!snapshotReady) {
      enqueueSnackbar('Primero debe calcular la sugerencia.', { variant: 'warning' });
      return;
    }
    try {
      setExportando(true);
      const allRows = await fetchAllFiltradas();
      if (allRows.length === 0) {
        enqueueSnackbar('No hay filas para exportar.', { variant: 'info' });
        return;
      }
      const exportRows = allRows.map((r) => {
        const cant = ediciones[r.codigoArticulo]?.cantidad ?? r.cantidadSugerida;
        return {
          Prioridad: r.prioridad,
          Código: r.codigoArticulo,
          Descripción: r.descripcion,
          Proveedor: r.nroProveedor ?? '',
          'Nombre proveedor': r.nombreProveedor || '',
          Ramo: r.codigoRamo,
          Línea: r.codigoLinea,
          Clase: r.codigoClase,
          'Stock neto': r.stockNeto,
          'Demanda mensual': r.demandaMensual,
          'Cobertura (días)': r.coberturaDias,
          'Lead time': r.leadTimeDias,
          Sugerido: r.cantidadSugerida,
          'Cant. a comprar': cant,
          'UM compra': r.umCompra,
          'Costo unit.': r.costoUnitario,
          Importe: Number((cant * r.costoUnitario).toFixed(2)),
          Consejo: r.consejo,
        };
      });
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sugerencias');
      XLSX.writeFile(wb, `SugerenciaCompra_${new Date().toISOString().slice(0, 10)}.xlsx`);
      enqueueSnackbar('Excel exportado correctamente.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo exportar el Excel.'), { variant: 'error' });
    } finally {
      setExportando(false);
      setExportProgressOpen(false);
    }
  };

  const handleIrPreview = async () => {
    if (!snapshotReady) {
      enqueueSnackbar('Primero debe calcular la sugerencia.', { variant: 'warning' });
      return;
    }
    try {
      const allRows = await fetchAllFiltradas();
      const lineasPreview = allRows
        .map((r) => ({
          codigoArticulo: r.codigoArticulo,
          cantidad: ediciones[r.codigoArticulo]?.cantidad ?? r.cantidadSugerida,
          nroProveedor: r.nroProveedor ?? undefined,
          costoUnitario: r.costoUnitario,
        }))
        .filter((x) => x.cantidad > 0);

      if (lineasPreview.length === 0) {
        enqueueSnackbar('No hay líneas con cantidad mayor a cero para generar órdenes.', { variant: 'warning' });
        return;
      }
      navigate('/compras/informes/sugerencia-compra/preview', {
        state: {
          lineas: lineasPreview,
          filtros: {
            nroProveedor,
            nombreProveedor,
            codigoRamo,
            codigoLinea,
            codigoClase,
            prioridad,
            horizonteDias,
          },
        },
      });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo preparar el preview.'), { variant: 'error' });
    } finally {
      setExportProgressOpen(false);
    }
  };

  const renderEstadoBanner = () => {
    if (calculando) {
      return (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
          Procesando sugerencia de compra… puede tardar varios minutos según el volumen de artículos.
        </Alert>
      );
    }
    if (!estado || estado.status === 'none') {
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No hay un cálculo generado. Configure los filtros y presione <strong>Calcular sugerencia</strong>.
        </Alert>
      );
    }
    if (estado.status === 'error') {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          El último cálculo falló: {estado.errorMessage || 'Error desconocido'}. Puede reintentar; se conserva el snapshot anterior si existía.
        </Alert>
      );
    }
    if (estado.isRecalculating) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          Recalculando… se muestran los datos del cálculo anterior ({formatFecha(estado.generatedAt)}).
        </Alert>
      );
    }
    if (estado.status === 'ready') {
      return (
        <Alert severity="success" sx={{ mb: 2 }}>
          Cálculo activo del {formatFecha(estado.generatedAt)} · {estado.totalItems ?? 0} artículos · {describeFiltrosCalculo(estado)}
          {estado.durationMs ? ` · ${Math.round(estado.durationMs / 1000)}s` : ''}
        </Alert>
      );
    }
    return null;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Sugerencia de compra
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={calculando ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
                onClick={() => void handleCalcular()}
                disabled={calculando}
              >
                {calculando ? 'Calculando…' : 'Calcular sugerencia'}
              </Button>
              <Button
                startIcon={<FileDownloadIcon />}
                variant="outlined"
                onClick={() => void handleExportExcel()}
                disabled={exportando || calculando || !snapshotReady}
              >
                {exportando ? 'Exportando...' : 'Exportar Excel'}
              </Button>
              <Button variant="contained" onClick={() => void handleIrPreview()} disabled={calculando || !snapshotReady}>
                Crear órdenes de compra
              </Button>
            </Stack>
          </Stack>

          {renderEstadoBanner()}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <ProveedorSelectorField
                label="Proveedor"
                value={
                  nroProveedor && nombreProveedor
                    ? {
                        nroProveedor,
                        nombre: nombreProveedor,
                        cuit: cuitProveedor || undefined,
                        tipoIva: tipoIvaProveedor || undefined,
                      }
                    : null
                }
                onSelect={handleProveedorSelect}
                onClear={handleProveedorClear}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <SearchableSelect label="Ramo" allowEmpty emptyOptionLabel="Todos" value={codigoRamo || null} onChange={(v) => { setCodigoRamo(v ?? ''); setPage(0); }} options={ramos} />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <SearchableSelect label="Línea" allowEmpty emptyOptionLabel="Todas" value={codigoLinea || null} onChange={(v) => { setCodigoLinea(v ?? ''); setPage(0); }} options={lineas} />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <SearchableSelect label="Clase" allowEmpty emptyOptionLabel="Todas" value={codigoClase || null} onChange={(v) => { setCodigoClase(v ?? ''); setPage(0); }} options={clases} />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <SearchableSelect
                label="Horizonte"
                value={String(horizonteDias)}
                onChange={(v) => setHorizonteDias(Number(v ?? 60))}
                options={[{ value: '30', label: '30d' }, { value: '60', label: '60d' }, { value: '90', label: '90d' }]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Ajuste demanda"
                value={ajusteDemandaPct}
                onChange={(e) => {
                  const parsed = Number(String(e.target.value).replace(',', '.'));
                  setAjusteDemandaPct(Number.isFinite(parsed) ? parsed : 0);
                }}
                inputProps={{ min: -100, max: 500, step: 1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                helperText="± sobre demanda esperada. Recalcule para aplicar."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  handleProveedorClear();
                  setCodigoRamo('');
                  setCodigoLinea('');
                  setCodigoClase('');
                  setHorizonteDias(60);
                  setAjusteDemandaPct(0);
                  setSearchTerm('');
                  setPrioridad('acomprar');
                  setPage(0);
                }}
              >
                Limpiar
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Buscar por código o descripción"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                disabled={!snapshotReady}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
            {prioridadChips.map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                color={prioridad === chip.key ? 'primary' : 'default'}
                disabled={!snapshotReady}
                onClick={() => { setPrioridad(chip.key); setPage(0); }}
              />
            ))}
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 1.5 }}><Typography variant="body2">Ítems críticos</Typography><Typography variant="h6">{kpis.itemsCriticos}</Typography></Paper></Grid>
            <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 1.5 }}><Typography variant="body2">Ítems a comprar</Typography><Typography variant="h6">{kpis.itemsAComprar}</Typography></Paper></Grid>
            <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 1.5 }}><Typography variant="body2">Importe estimado</Typography><Typography variant="h6">{formatCurrency(kpis.importeEstimado)}</Typography></Paper></Grid>
            <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 1.5 }}><Typography variant="body2">Cobertura mediana</Typography><Typography variant="h6">{kpis.coberturaMediana.toFixed(2)} días</Typography></Paper></Grid>
          </Grid>

          <TableContainer component={Paper} sx={{ borderRadius: 2, position: 'relative', minHeight: 120 }}>
            {loadingGrid && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)', zIndex: 1 }}>
                <CircularProgress size={32} />
              </Box>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Prioridad</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Proveedor</TableCell>
                  <TableCell align="right">Stock neto</TableCell>
                  <TableCell align="right">Pendiente OC</TableCell>
                  <TableCell align="right">Demanda mensual</TableCell>
                  <TableCell align="right">Cobertura</TableCell>
                  <TableCell align="right">Lead time</TableCell>
                  <TableCell align="right">Sugerido</TableCell>
                  <TableCell align="right">Cant. a comprar</TableCell>
                  <TableCell align="right">Costo</TableCell>
                  <TableCell align="right">Importe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsConEdicion.map((row) => {
                  const importe = row.cantidadFinal * row.costoUnitario;
                  return (
                    <TableRow key={row.codigoArticulo}>
                      <TableCell>{row.prioridad}</TableCell>
                      <TableCell>{row.codigoArticulo}</TableCell>
                      <TableCell>{row.descripcion}</TableCell>
                      <TableCell>{row.nroProveedor ?? '-'} {row.nombreProveedor ? `- ${row.nombreProveedor}` : ''}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                          <Typography variant="body2" component="span">
                            {row.stockNeto.toFixed(2)}
                          </Typography>
                          <Tooltip title="Ver composición del stock neto por depósito">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={`Ver composición de stock neto de ${row.codigoArticulo}`}
                                disabled={!snapshotReady}
                                onClick={() => {
                                  setStockNetoDialogItem(row);
                                  setStockNetoDialogOpen(true);
                                }}
                              >
                                <Inventory2OutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{row.stockPendienteOc.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                          <Typography variant="body2" component="span">
                            {row.demandaMensual.toFixed(2)}
                          </Typography>
                          <Tooltip title="Ver evolución de demanda (12 meses)">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={`Ver evolución de demanda de ${row.codigoArticulo}`}
                                disabled={!snapshotReady}
                                onClick={() => {
                                  setDemandaDialogItem(row);
                                  setDemandaDialogOpen(true);
                                }}
                              >
                                <ShowChartOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{row.coberturaDias.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.leadTimeDias.toFixed(0)}</TableCell>
                      <TableCell align="right">{row.cantidadSugerida.toFixed(3)}</TableCell>
                      <TableCell align="right">
                        <DecimalInput value={row.cantidadFinal} onChange={(value) => setCantidadEditada(row.codigoArticulo, value)} />
                      </TableCell>
                      <TableCell align="right">${row.costoUnitario.toFixed(2)}</TableCell>
                      <TableCell align="right">{formatCurrency(importe)}</TableCell>
                    </TableRow>
                  );
                })}
                {!loadingGrid && rowsConEdicion.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      {snapshotReady ? 'Sin resultados para los filtros aplicados.' : 'Calcule la sugerencia para ver resultados.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas"
            disabled={!snapshotReady}
          />
        </CardContent>
      </Card>

      <ExportProgressDialog open={exportProgressOpen} progress={exportProgress} />
      <DemandaHistoricaDialog
        open={demandaDialogOpen}
        onClose={() => {
          setDemandaDialogOpen(false);
          setDemandaDialogItem(null);
        }}
        item={demandaDialogItem}
      />
      <StockNetoComposicionDialog
        open={stockNetoDialogOpen}
        onClose={() => {
          setStockNetoDialogOpen(false);
          setStockNetoDialogItem(null);
        }}
        item={stockNetoDialogItem}
      />
    </Box>
  );
};

export default SugerenciaCompra;
